import React, { useState } from 'react'
import { Card, Button, Icon, Collapse } from '@blueprintjs/core'
import { useSegmentationPriorities } from '../../contexts/SegmentationPrioritiesProvider'
import { AppToaster } from '../toaster'
import { SegmentationScoresConstraints } from './SegmentationScoresConstraints'
import './SegmentationScoresController.css'

/**
 * SegmentationScoresController displays the selection status and provides score calculation control.
 * @param {Object} props - Component props
 * @param {Array} props.selectedSegmentations - Currently selected segmentations
 * @param {Function} props.onFetchScores - Callback when scores should be fetched
 * @param {boolean} props.hasExistingScores - Whether there are existing scores
 * @param {boolean} props.hasSelectionChanged - Whether selection has changed
 * @param {boolean} props.needsRecalculationFlag - Whether recalculation is needed
 * @param {boolean} props.isFetchingScores - Whether scores are being fetched
 * @param {Function} [props.onOpenHelp] - Opens the help drawer (optional)
 * @returns {JSX.Element} The controller component
 */
export function SegmentationScoresController({
  selectedSegmentations,
  onFetchScores,
  hasExistingScores,
  hasSelectionChanged,
  needsRecalculationFlag,
  isFetchingScores,
  onOpenHelp,
}) {
  // Collapse state for advanced settings (default collapsed for a compact UI)
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false)
  const toggleAdvancedSettings = () => setIsAdvancedSettingsOpen(prev => !prev)

  const {
    useRespondentsConstraint,
    minRespondentsPerSegment,
    useDistributionConstraint,
    maxProperlyDistributedThreshold,
  } = useSegmentationPriorities()

  const handleFetchScores = async () => {
    if (selectedSegmentations.length === 0) {
      AppToaster.show({
        message: 'Please select at least one segmentation to get scores for.',
        intent: 'warning',
        icon: 'warning-sign',
      })
      return
    }

    await onFetchScores({
      useRespondentsConstraint,
      minRespondentsPerSegment,
      useDistributionConstraint,
      maxProperlyDistributedThreshold,
    })
  }

  // Button logic
  const isGetScoresDisabled = () => {
    return (
      selectedSegmentations.length === 0 ||
      (!needsRecalculationFlag && !hasSelectionChanged)
    )
  }

  const getStatusIntent = () => {
    if (selectedSegmentations.length === 0) return 'warning'
    if (hasSelectionChanged) return 'primary'
    if (needsRecalculationFlag) return 'warning'
    return 'success'
  }

  const getStatusIcon = () => {
    if (selectedSegmentations.length === 0) return 'warning-sign'
    if (hasSelectionChanged) return 'refresh'
    if (needsRecalculationFlag) return 'warning-sign'
    return 'tick-circle'
  }

  const getStatusCssClass = () => {
    if (selectedSegmentations.length === 0)
      return 'status-message--no-selection'
    if (hasSelectionChanged) return 'status-message--selection-changed'
    if (needsRecalculationFlag) return 'status-message--needs-recalculation'
    return 'status-message--up-to-date'
  }

  const getStatusMessage = () => {
    if (selectedSegmentations.length === 0) {
      return 'No segmentations selected. Please select segmentations from the left panel.'
    }

    let message = `${selectedSegmentations.length} segmentation(s) selected`

    if (hasSelectionChanged) {
      message += ' - Click "Get Scores" to calculate'
    } else if (needsRecalculationFlag && hasExistingScores) {
      message +=
        ' - Priorities or Constraints changed, scores need recalculation'
    } else if (hasExistingScores) {
      message += ' - Scores are up to date'
    }

    // Append concise constraints summary on a single line with chevron and tabs
    const constraintsSummary = `\nConstraints \u203A\u203A\t Minimum respondents per segment: ${
      useRespondentsConstraint ? `${minRespondentsPerSegment}` : 'Off'
    }  -  Balanced segments distribution ratio (max/min): ${
      useDistributionConstraint ? `< ${maxProperlyDistributedThreshold}` : 'Off'
    }`
    message += constraintsSummary

    return message
  }

  return (
    <Card className="segmentation-scores-controller">
      <div className="controller-header">
        <div
          className="controller-header-top"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Icon icon="selection" size={16} className="controller-header-icon" />
          <h4 className="controller-title" style={{ margin: 0 }}>
            Selected Segmentations Status
          </h4>
        </div>
      </div>

      {/* Status message */}
      <div className={`status-message ${getStatusCssClass()}`}>
        <Icon
          icon={getStatusIcon()}
          intent={getStatusIntent()}
          className="status-message-icon"
        />
        <span className="status-message-text">{getStatusMessage()}</span>
      </div>

      {/* Advanced Settings (collapsible) */}
      <div
        className="advanced-settings-header"
        onClick={toggleAdvancedSettings}
        aria-expanded={isAdvancedSettingsOpen}
        aria-controls="advance-settings-constraints"
        role="button"
        tabIndex={0}
        style={{ marginBottom: '10px' }}
      >
        <h5
          className="advanced-settings-title"
          style={{ margin: 0, paddingLeft: 10, flex: 1 }}
        >
          Advanced settings{'  '} <Icon icon="cog" size={12} />
        </h5>
        <Icon icon={isAdvancedSettingsOpen ? 'caret-down' : 'caret-right'} />
      </div>

      {/* Constraints controls (collapsible) */}
      <Collapse
        isOpen={isAdvancedSettingsOpen}
        keepChildrenMounted
        id="advance-settings-constraints"
      >
        <SegmentationScoresConstraints />
      </Collapse>

      {/* Get Scores Button + Help */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center', // Center horizontally
          gap: 4,
        }}
      >
        <Button
          style={{ borderRadius: 4 }}
          intent="primary"
          icon="th"
          onClick={handleFetchScores}
          text="Get Scores"
          disabled={isGetScoresDisabled()}
          loading={isFetchingScores}
        />
        <Button
          icon="help"
          minimal
          small
          onClick={() => onOpenHelp && onOpenHelp()}
          aria-label="Open scoring help"
        />
      </div>

      {/* Help text for first time users */}
      {!hasExistingScores && (
        <div className="help-section">
          <div className="help-content">
            <Icon icon="info-sign" intent="primary" className="help-icon" />
            <div>
              <p className="help-title">To calculate scores, ensure:</p>
              <ul className="help-list">
                <li>At least one segmentation priority exists</li>
                <li>At least one priority has a weight greater than zero</li>
                <li>Select segmentations from the left panel</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}
